import React, { useState, useCallback } from "react";
import AppHeader from "./AppHeader";
import AppSidebar from "./AppSidebar";

export const SIDEBAR_COLLAPSED_KEY = "succeedo_sidebar_collapsed";
// Below this width (tablets in landscape) a page that asks for it starts with
// the sidebar collapsed, unless the owner has already chosen.
export const COMPACT_BELOW_PX = 1280;

function readCollapsedPreference() {
  try {
    const saved = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (saved === "1") return true;
    if (saved === "0") return false;
  } catch (_e) {
    // Private mode or blocked storage: fall back to the page default.
  }
  return null;
}

export function initialCollapsed(compactOnTablet) {
  if (typeof window === "undefined") return false;
  const saved = readCollapsedPreference();
  if (saved !== null) return saved;
  return !!compactOnTablet && window.innerWidth < COMPACT_BELOW_PX;
}

// `isPartner` intentionally has no default — when the caller doesn't
// specify, AppHeader and AppSidebar each self-detect via the Apollo
// cache (`currentUser.coach_profile.is_partner`). Defaulting to `false`
// here would short-circuit that detection and force partners into the
// owner shell.
export default function AppLayout({
  children,
  breadcrumb = "DASHBOARD",
  persona,
  userName,
  userInitials,
  hasNotifications = true,
  isPartner,
  // No defaults: a page that doesn't pass its owner's real plan shows no
  // progress and no badge rather than placeholder numbers.
  planProgress,
  actionPlanBadge,
  // Start collapsed on tablets when the owner hasn't chosen (the George
  // page, where the conversation and the Actions panel need the room).
  compactOnTablet = false,
}) {
  // Tablet and desktop: the sidebar collapses to an icon rail. Phones keep
  // the slide-out drawer. The choice is remembered per browser.
  const [collapsed, setCollapsed] = useState(() => initialCollapsed(compactOnTablet));
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch (_e) {
        // Not remembered, but the toggle still works on this page.
      }
      return next;
    });
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--page-bg))' }}>
      <AppHeader
        breadcrumb={breadcrumb}
        persona={persona}
        userName={userName}
        userInitials={userInitials}
        hasNotifications={hasNotifications}
        sidebarCollapsed={collapsed}
        onToggleSidebar={toggleCollapsed}
      />

      <AppSidebar
        persona={persona}
        isPartner={isPartner}
        planProgress={planProgress}
        actionPlanBadge={actionPlanBadge}
        collapsed={collapsed}
      />

      <main
        className={`pt-14 transition-[margin] duration-300 ${collapsed ? "md:ml-[64px]" : "md:ml-[240px]"}`}
      >
        {children}
      </main>
    </div>
  );
}
